
import { v4 as uuidv4 } from "uuid";
import { 
  FloatingFeature,
  IStringHashFeature 
} from "@models";
import { 
  AbilityEffect,
  CharacterFeature,
  ConsumedResource,
  ExtraEffectsFeature,
  ExtraEffectTypeFeature,
  SpellDamageOptions,
  SpellLimitation,
  SpellModifier,
  UpgradableNumber
} from "@dndModels";
import { AbilityScoreExpandedType, CastingTime, CostOverrideType, DurationType, SpellLevel } from "@dndModels/Types";
import { STANDARD_CASTING_TIMES } from "@dndModels/Constants";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class SpellOverrides {
  components_override: ("Don't Override" | "No V" | "No S" | "No M" | "No Consume" | "V" | "S" | "M")[] = ["Don't Override"]; // VSM, 
  material_component_override = "";
  casting_time_override: "Don't Override" | "Options" | CastingTime = "Don't Override"; // A, BA, RA, X minute(s), etc
  casting_time_options: (CastingTime | "Don't Override")[] = ["Don't Override"];
  
  cost_override: OverridableCost = new OverridableCost();
  get slot_override() {
    return this.cost_override.slot_override;
  }
  set slot_override(value: CostOverrideType) {
    this.cost_override.slot_override = value;
  }
  get consumed_resource() {
    return this.cost_override.consumed_resource;
  }
  set consumed_resource(value: ConsumedResource) {
    this.cost_override.consumed_resource = value;
  }
  
  concentration_override: "Don't Override" | "Concentration" | "No Concentration" = "Don't Override";
  reaction_override: "Don't Override" | "Reaction" | "No Reaction" = "Don't Override";
  ritual_override: "Don't Override" | "Ritual" | "No Ritual" | "Ritual Only" = "Don't Override";
  
  duration_override: DurationType | "Multiply" | "Don't Override" = "Don't Override";
  special_duration = "";
  duration_multiplier = 1;

  range_override: "Don't Override" | "Replace" | "Add" | "Multiply" = "Don't Override"; // A, BA, RA, X minute(s), etc
  range_override_num: UpgradableNumber = new UpgradableNumber();

  count_as_class_id = "Don't Override";
  spellcasting_ability: AbilityScoreExpandedType | "Don't Override" = "Don't Override";
  spellcasting_ability_selectable_key = "";
  override_dc_atk_mod = false;
  spell_dc: UpgradableNumber = new UpgradableNumber();
  spell_attack: UpgradableNumber = new UpgradableNumber();
  spell_mod: UpgradableNumber = new UpgradableNumber();
  cast_at_level: SpellLevel | "Don't Override" = "Don't Override";

  // modifies: "Spell DC" | "Attack" | "Damage" | "Range" | "Casting Time" | "Bonus Effect" | "Include Modifier" | "Minimum Damage" | "Damage Reroll" | "None" = "None";
  minimum_damage_die_roll = 0;
  max_dice_minned = 0;
  damage_die_reroll_threshold = 0;
  max_dice_rerolled = 0;
  include_modifier_on_damage: "Don't Override" | "True" | "False" = "Don't Override";
  damage_die_size_override = -1;
  maximize_dice_roll = false;
  apply_overrides_on_slot_casting = false;
  // Extra Effects
  save_dc: UpgradableNumber = new UpgradableNumber();
  attack_bonus: UpgradableNumber = new UpgradableNumber();
  potence_type = "None"; // Character, Class, or None
  potence_class = "";
  effects: AbilityEffect[] = [];

  char_feature: CharacterFeature = new CharacterFeature();
  
  constructor(obj?: any) {
    if (obj) {
      this.components_override = obj.components_override ? [...obj.components_override] : ["Don't Override"];
      this.material_component_override = obj.material_component_override ? obj.material_component_override : "";
      this.casting_time_override = obj.casting_time_override ? obj.casting_time_override : "Don't Override";
      if (["","Normal"].includes(this.casting_time_override)) {
        this.casting_time_override = "Don't Override";
      }
      this.casting_time_options = obj.casting_time_options ? obj.casting_time_options : ["Don't Override"];
      
      if (obj.cost_override) {
        this.cost_override = new OverridableCost(obj.cost_override);
      } else {
        this.cost_override = new OverridableCost();
        this.slot_override = obj.slot_override ? obj.slot_override : "Don't Override";
        this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
        if (!obj.consumed_resource) {
          this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
          this.consumed_resource.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
          this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
          this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "Long Rest";
          this.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
          this.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
        }
      }
      
      this.concentration_override = obj.concentration_override ? obj.concentration_override : "Don't Override";
      this.reaction_override = obj.reaction_override ? obj.reaction_override : "Don't Override";
      this.ritual_override = obj.ritual_override ? obj.ritual_override : "Don't Override";

      this.duration_override = obj.duration_override ? obj.duration_override : "Don't Override";
      this.special_duration = obj.special_duration ? obj.special_duration : "";
      this.duration_multiplier = obj.duration_multiplier ? obj.duration_multiplier : 1;

      this.range_override = obj.range_override ? obj.range_override : "Don't Override";
      this.range_override_num = obj.range_override_num ? new UpgradableNumber(obj.range_override_num) : new UpgradableNumber();
      
      this.count_as_class_id = obj.count_as_class_id && obj.count_as_class_id !== "" && obj.count_as_class_id !== "None" ? obj.count_as_class_id : "Don't Override";
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "Don't Override";
      this.spellcasting_ability_selectable_key = obj.spellcasting_ability_selectable_key ? obj.spellcasting_ability_selectable_key : "";
      this.override_dc_atk_mod = obj.override_dc_atk_mod ? obj.override_dc_atk_mod : false;
      this.spell_dc = obj.spell_dc ? new UpgradableNumber(obj.spell_dc) : new UpgradableNumber();
      this.spell_attack = obj.spell_attack ? new UpgradableNumber(obj.spell_attack) : new UpgradableNumber();
      this.spell_mod = obj.spell_mod ? new UpgradableNumber(obj.spell_mod) : new UpgradableNumber();
      this.cast_at_level = obj.cast_at_level ? obj.cast_at_level : "Don't Override";

      this.minimum_damage_die_roll = obj.minimum_damage_die_roll ? obj.minimum_damage_die_roll : 0;
      this.max_dice_minned = obj.max_dice_minned ? obj.max_dice_minned : 0;
      this.damage_die_reroll_threshold = obj.damage_die_reroll_threshold ? obj.damage_die_reroll_threshold : 0;
      this.max_dice_rerolled = obj.max_dice_rerolled ? obj.max_dice_rerolled : 0;
      this.include_modifier_on_damage = obj.include_modifier_on_damage ? obj.include_modifier_on_damage : "Don't Override";
      this.damage_die_size_override = obj.damage_die_size_override !== undefined ? obj.damage_die_size_override : -1;
      this.maximize_dice_roll = obj.maximize_dice_roll ? obj.maximize_dice_roll : false;

      this.apply_overrides_on_slot_casting = obj.apply_overrides_on_slot_casting ? obj.apply_overrides_on_slot_casting : false;
      
      // Extra Effects
      this.potence_type = obj.potence_type ? obj.potence_type : "None";
      this.potence_class = obj.potence_class ? obj.potence_class : "";
      this.effects = obj.effects ? obj.effects.map((o: any) => new AbilityEffect(o)) : [];
      this.save_dc = obj.save_dc ? new UpgradableNumber(obj.save_dc) : new UpgradableNumber();
      this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber(); 
    }
  }

  get use_apply_overrides_on_slot_casting() {
    if (["At Will","Only Special Resource","Don't Override","And Special Resource"].includes(this.slot_override)) {
      return true;
    } else {
      return this.apply_overrides_on_slot_casting;
    }
  }

  get name() {
    return this.char_feature.name;
  }

  get description() {
    return this.char_feature.description;
  }

  get true_id() {
    return this.char_feature.true_id;
  }

  toDBObj(): any {
    return {
      components_override: this.components_override,
      material_component_override: this.material_component_override,
      casting_time_override: this.casting_time_override,
      casting_time_options: this.casting_time_options,

      cost_override: this.cost_override.toDBObj(),

      concentration_override: this.concentration_override,
      reaction_override: this.reaction_override,
      ritual_override: this.ritual_override,
      
      duration_override: this.duration_override,
      special_duration: this.special_duration,
      duration_multiplier: this.duration_multiplier,

      range_override: this.range_override,
      range_override_num: this.range_override_num.toDBObj(),

      count_as_class_id: this.count_as_class_id,
      spellcasting_ability: this.spellcasting_ability,
      spellcasting_ability_selectable_key: this.spellcasting_ability_selectable_key,
      override_dc_atk_mod: this.override_dc_atk_mod,
      spell_dc: this.spell_dc.toDBObj(),
      spell_attack: this.spell_attack.toDBObj(),
      spell_mod: this.spell_mod.toDBObj(),
      cast_at_level: this.cast_at_level,

      minimum_damage_die_roll: this.minimum_damage_die_roll,
      max_dice_minned: this.max_dice_minned,
      damage_die_reroll_threshold: this.damage_die_reroll_threshold,
      max_dice_rerolled: this.max_dice_rerolled,
      include_modifier_on_damage: this.include_modifier_on_damage,
      damage_die_size_override: this.damage_die_size_override,
      maximize_dice_roll: this.maximize_dice_roll,

      apply_overrides_on_slot_casting: this.apply_overrides_on_slot_casting,

      // Extra Effects
      potence_type: this.potence_type,
      potence_class: this.potence_class,
      effects: this.effects.map(o => o.toDBObj()),
      save_dc: this.save_dc.toDBObj(),
      attack_bonus: this.attack_bonus.toDBObj()
    };
  }

  copy(copyMe: SpellOverrides): void {
    this.components_override = [...copyMe.components_override];
    this.material_component_override = copyMe.material_component_override;
    this.casting_time_override = copyMe.casting_time_override;
    this.casting_time_options = [...copyMe.casting_time_options];
    
    this.cost_override = copyMe.cost_override.clone();
    
    this.concentration_override = copyMe.concentration_override;
    this.reaction_override = copyMe.reaction_override;
    this.ritual_override = copyMe.ritual_override;

    this.duration_override = copyMe.duration_override;
    this.special_duration = copyMe.special_duration;
    this.duration_multiplier = copyMe.duration_multiplier;

    this.range_override = copyMe.range_override;
    this.range_override_num = copyMe.range_override_num.clone();
    
    this.count_as_class_id = copyMe.count_as_class_id;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spellcasting_ability_selectable_key = copyMe.spellcasting_ability_selectable_key;
    this.override_dc_atk_mod = copyMe.override_dc_atk_mod;
    this.spell_dc = copyMe.spell_dc.clone();
    this.spell_attack = copyMe.spell_attack.clone();
    this.spell_mod = copyMe.spell_mod.clone();
    this.cast_at_level = copyMe.cast_at_level;

    this.minimum_damage_die_roll = copyMe.minimum_damage_die_roll;
    this.max_dice_minned = copyMe.max_dice_minned;
    this.damage_die_reroll_threshold = copyMe.damage_die_reroll_threshold;
    this.max_dice_rerolled = copyMe.max_dice_rerolled;
    this.include_modifier_on_damage = copyMe.include_modifier_on_damage;
    this.damage_die_size_override = copyMe.damage_die_size_override;
    this.maximize_dice_roll = copyMe.maximize_dice_roll;

    this.apply_overrides_on_slot_casting = copyMe.apply_overrides_on_slot_casting;
    
    // Extra Effects
    this.potence_type = copyMe.potence_type;
    this.potence_class = copyMe.potence_class;
    this.effects = copyMe.effects.map(o => o.clone());
    this.save_dc = copyMe.save_dc.clone();
    this.attack_bonus = copyMe.attack_bonus.clone();
  }

  clone(): SpellOverrides {
    const new_obj = new SpellOverrides();
    new_obj.copy(this);
    return new_obj;
  }
}

export class SpellsFromList extends FloatingFeature {
  _name = "";
  // Spell Limits
  saa_type: "Standard" | "Extra" = "Standard";
  spell_limit: SpellLimitation = new SpellLimitation();
  // Suggestions
  suggestions: string[] = [];
  count: UpgradableNumber = new UpgradableNumber(1);
  // Alterations
  spell_overrides: SpellOverrides = new SpellOverrides();
  
  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Spells from List";
    if (obj) {
      // This one wasn't in SpellsFromList before merging SpellAsAbility into it.  
      // It's used to make Spell Scrolls, and similar items work, 
      // where there's a spell extra passed to it.
      this.saa_type = obj.saa_type ? obj.saa_type : "Standard";
      this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
      this.suggestions = [];
      if (obj.suggestions) {
        this.suggestions = [...obj.suggestions];
      }
      this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
      if (!obj.spell_limit) {
        const allow_rituals: boolean = obj.allow_rituals !== undefined ? obj.allow_rituals : null;
        const ritual_only: boolean = allow_rituals === true && obj.ritual_only ? obj.ritual_only : false;
        this.spell_limit.ritual = ritual_only ? "Yes" : (allow_rituals ? "Ignore" : "No");
        let casting_times: CastingTime[] = obj.casting_times ? obj.casting_times : [];
        let special_casting_time: string = obj.special_casting_time ? obj.special_casting_time : "";
        let i = 0;
        while (i < casting_times.length) {
          if (!STANDARD_CASTING_TIMES.includes(casting_times[i])) {
            special_casting_time = casting_times[i];
            casting_times.push("Special");
             
            casting_times = casting_times.filter(o => o !== special_casting_time);
          } else {
            ++i;
          }
        }
        this.spell_limit.casting_times = casting_times;
        this.spell_limit.special_casting_time = special_casting_time;
        
        const specific_spell_ids: string[] = obj.specific_spell_ids ? obj.specific_spell_ids : [];
        this.spell_limit.spell_ids = specific_spell_ids;
        
        this.spell_limit.list_ids = obj.list_ids ? obj.list_ids : (obj.list_id ? [obj.list_id] : ["Any"]);
        this.spell_limit.spell_list_selectable_key = obj.spell_list_selectable_key ? obj.spell_list_selectable_key : (obj.selectable_key ? obj.selectable_key : "");
        this.spell_limit.schools = obj.magic_schools ? obj.magic_schools : ["Any"];
        this.spell_limit.school_selectable_key = obj.magic_school_selectable_key ? obj.magic_school_selectable_key : "";

        let levels: string[] = [];
        if (obj.levels) {
          levels = obj.levels;
        } else if (obj.max_level !== undefined) {
          const max_level = +obj.max_level;
          if (max_level === 0) {
            levels.push("Cantrips");
          } else {
            for (let i = 1; i <= max_level; ++i) {
              levels.push(`${i}`);
            }
          }
        } else {
          levels.push("Any");
        }
        if (levels.includes("Any")) {
          this.spell_limit.levels = [];
        } else if (levels.includes("Use Min/Max")) {
          this.spell_limit.levels = [-1];
          this.spell_limit.min_level = obj.min_level ? new UpgradableNumber(obj.min_level) : new UpgradableNumber();
          this.spell_limit.max_level = obj.max_level ? new UpgradableNumber(obj.max_level) : new UpgradableNumber(9);
        } else if (levels.includes("Leveled Spells")) {
          this.spell_limit.levels = [1,2,3,4,5,6,7,8,9];
        } else if (!levels.includes("Char Max")) {
          this.spell_limit.levels = levels.map(o => {
            const o2 = +o;
            if (o2 === 0 || o2 === 1 || o2 === 2 || o2 === 3 || o2 === 4 || o2 === 5 || o2 === 6 || o2 === 7 || o2 === 8 || o2 === 9) {
              return o2;
            }
            return 0;
          });
        }
      }

      this.spell_overrides = obj.spell_overrides ? new SpellOverrides(obj.spell_overrides) : new SpellOverrides();
      if (!obj.spell_overrides) {
        this.spell_overrides.components_override = obj.components_override ? [...obj.components_override] : ["Don't Override"];
        this.spell_overrides.material_component_override = obj.material_component_override ? obj.material_component_override : "Don't Override";
        this.spell_overrides.casting_time_override = obj.casting_time_override ? obj.casting_time_override : "Don't Override";
        this.spell_overrides.slot_override = obj.slot_override ? obj.slot_override : "Don't Override";
        
        this.spell_overrides.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
        if (!obj.consumed_resource) {
          this.spell_overrides.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "Don't Override";
          this.spell_overrides.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
          this.spell_overrides.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
          this.spell_overrides.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
          if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
            // Translate old set up to new
            this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber();
            this.spell_overrides.consumed_resource.special_resource_amount.base = obj.special_resource_amount;
          } else if (obj.special_resource_amount) {
            this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
          } else {
            this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber();
          }
          this.spell_overrides.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
        }
        this.spell_overrides.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "CHA";
        this.spell_overrides.spellcasting_ability_selectable_key = obj.spellcasting_ability_selectable_key ? obj.spellcasting_ability_selectable_key : "";
        const at_will = obj.at_will ? obj.at_will : false;
        if (at_will) {
          this.spell_overrides.slot_override = "At Will";
        }
        this.spell_overrides.concentration_override = obj.concentration_override ? obj.concentration_override : "Don't Override";
        this.spell_overrides.duration_override = obj.duration_override ? obj.duration_override : "Don't Override";
        this.spell_overrides.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : "None";
        if (["","Normal"].includes(this.spell_overrides.casting_time_override)) {
          this.spell_overrides.casting_time_override = "Don't Override";
        }
        if (["","Normal"].includes(this.spell_overrides.slot_override)) {
          this.spell_overrides.slot_override = "Don't Override";
        }
        if (["","Normal"].includes(this.spell_overrides.material_component_override)) {
          this.spell_overrides.material_component_override = "Don't Override";
        }
        if (["","Normal"].includes(this.spell_overrides.consumed_resource.resource_consumed)) {
          this.spell_overrides.consumed_resource.resource_consumed = "Don't Override";
        }
        if (this.spell_overrides.components_override.length === 0 || 
          (this.spell_overrides.components_override.length === 1 && 
            ["","Normal"].includes(this.spell_overrides.components_override[0]))) {
          this.spell_overrides.components_override = ["Don't Override"];
        }
        if (obj.spellcasting_ability === "") {
          this.spell_overrides.spellcasting_ability = "Don't Override";
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     // This one wasn't in SpellsFromList before merging SpellAsAbility into it.  
  //     // It's used to make Spell Scrolls, and similar items work, 
  //     // where there's a spell extra passed to it.
  //     this.saa_type = obj.saa_type ? obj.saa_type : "Standard";
  //     this.count = obj.count ? new UpgradableNumber(obj.count) : new UpgradableNumber(1);
  //     this.suggestions = [];
  //     if (obj.suggestions) {
  //       this.suggestions = [...obj.suggestions];
  //     }
  //     this.spell_limit = obj.spell_limit ? new SpellLimitation(obj.spell_limit) : new SpellLimitation();
  //     if (!obj.spell_limit) {
  //       const allow_rituals: boolean = obj.allow_rituals !== undefined ? obj.allow_rituals : null;
  //       const ritual_only: boolean = allow_rituals === true && obj.ritual_only ? obj.ritual_only : false;
  //       this.spell_limit.ritual = ritual_only ? "Yes" : (allow_rituals ? "Ignore" : "No");
  //       let casting_times: CastingTime[] = obj.casting_times ? obj.casting_times : [];
  //       let special_casting_time: string = obj.special_casting_time ? obj.special_casting_time : "";
  //       let i = 0;
  //       while (i < casting_times.length) {
  //         if (!STANDARD_CASTING_TIMES.includes(casting_times[i])) {
  //           special_casting_time = casting_times[i];
  //           casting_times.push("Special");
  //           // eslint-disable-next-line
  //           casting_times = casting_times.filter(o => o !== special_casting_time);
  //         } else {
  //           ++i;
  //         }
  //       }
  //       this.spell_limit.casting_times = casting_times;
  //       this.spell_limit.special_casting_time = special_casting_time;
        
  //       const specific_spell_ids: string[] = obj.specific_spell_ids ? obj.specific_spell_ids : [];
  //       this.spell_limit.spell_ids = specific_spell_ids;
        
  //       this.spell_limit.list_ids = obj.list_ids ? obj.list_ids : (obj.list_id ? [obj.list_id] : ["Any"]);
  //       this.spell_limit.spell_list_selectable_key = obj.spell_list_selectable_key ? obj.spell_list_selectable_key : (obj.selectable_key ? obj.selectable_key : "");
  //       this.spell_limit.schools = obj.magic_schools ? obj.magic_schools : ["Any"];
  //       this.spell_limit.school_selectable_key = obj.magic_school_selectable_key ? obj.magic_school_selectable_key : "";

  //       let levels: string[] = [];
  //       if (obj.levels) {
  //         levels = obj.levels;
  //       } else if (obj.max_level !== undefined) {
  //         const max_level = +obj.max_level;
  //         if (max_level === 0) {
  //           levels.push("Cantrips");
  //         } else {
  //           for (let i = 1; i <= max_level; ++i) {
  //             levels.push(`${i}`);
  //           }
  //         }
  //       } else {
  //         levels.push("Any");
  //       }
  //       if (levels.includes("Any")) {
  //         this.spell_limit.levels = [];
  //       } else if (levels.includes("Use Min/Max")) {
  //         this.spell_limit.levels = [-1];
  //         this.spell_limit.min_level = obj.min_level ? new UpgradableNumber(obj.min_level) : new UpgradableNumber();
  //         this.spell_limit.max_level = obj.max_level ? new UpgradableNumber(obj.max_level) : new UpgradableNumber(9);
  //       } else if (levels.includes("Leveled Spells")) {
  //         this.spell_limit.levels = [1,2,3,4,5,6,7,8,9];
  //       } else if (!levels.includes("Char Max")) {
  //         this.spell_limit.levels = levels.map(o => {
  //           const o2 = +o;
  //           if (o2 === 0 || o2 === 1 || o2 === 2 || o2 === 3 || o2 === 4 || o2 === 5 || o2 === 6 || o2 === 7 || o2 === 8 || o2 === 9) {
  //             return o2;
  //           }
  //           return 0;
  //         });
  //       }
  //     }

  //     this.spell_overrides = obj.spell_overrides ? new SpellOverrides(obj.spell_overrides) : new SpellOverrides();
  //     if (!obj.spell_overrides) {
  //       this.spell_overrides.components_override = obj.components_override ? [...obj.components_override] : ["Don't Override"];
  //       this.spell_overrides.material_component_override = obj.material_component_override ? obj.material_component_override : "Don't Override";
  //       this.spell_overrides.casting_time_override = obj.casting_time_override ? obj.casting_time_override : "Don't Override";
  //       this.spell_overrides.slot_override = obj.slot_override ? obj.slot_override : "Don't Override";
        
  //       this.spell_overrides.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //       if (!obj.consumed_resource) {
  //         this.spell_overrides.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "Don't Override";
  //         this.spell_overrides.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
  //         this.spell_overrides.consumed_resource.slot_level = obj.slot_level ? obj.slot_level : 1;
  //         this.spell_overrides.consumed_resource.slot_type = obj.slot_type ? obj.slot_type : "";
  //         if (obj.special_resource_amount && obj.special_resource_amount.base === undefined) {
  //           // Translate old set up to new
  //           this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber();
  //           this.spell_overrides.consumed_resource.special_resource_amount.base = obj.special_resource_amount;
  //         } else if (obj.special_resource_amount) {
  //           this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber(obj.special_resource_amount);
  //         } else {
  //           this.spell_overrides.consumed_resource.special_resource_amount = new UpgradableNumber();
  //         }
  //         this.spell_overrides.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //       }
  //       this.spell_overrides.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "CHA";
  //       this.spell_overrides.spellcasting_ability_selectable_key = obj.spellcasting_ability_selectable_key ? obj.spellcasting_ability_selectable_key : "";
  //       const at_will = obj.at_will ? obj.at_will : false;
  //       if (at_will) {
  //         this.spell_overrides.slot_override = "At Will";
  //       }
  //       this.spell_overrides.concentration_override = obj.concentration_override ? obj.concentration_override : "Don't Override";
  //       this.spell_overrides.duration_override = obj.duration_override ? obj.duration_override : "Don't Override";
  //       this.spell_overrides.count_as_class_id = obj.count_as_class_id ? obj.count_as_class_id : "None";
  //       if (["","Normal"].includes(this.spell_overrides.casting_time_override)) {
  //         this.spell_overrides.casting_time_override = "Don't Override";
  //       }
  //       if (["","Normal"].includes(this.spell_overrides.slot_override)) {
  //         this.spell_overrides.slot_override = "Don't Override";
  //       }
  //       if (["","Normal"].includes(this.spell_overrides.material_component_override)) {
  //         this.spell_overrides.material_component_override = "Don't Override";
  //       }
  //       if (["","Normal"].includes(this.spell_overrides.consumed_resource.resource_consumed)) {
  //         this.spell_overrides.consumed_resource.resource_consumed = "Don't Override";
  //       }
  //       if (this.spell_overrides.components_override.length === 0 || 
  //         (this.spell_overrides.components_override.length === 1 && 
  //           ["","Normal"].includes(this.spell_overrides.components_override[0]))) {
  //         this.spell_overrides.components_override = ["Don't Override"];
  //       }
  //       if (obj.spellcasting_ability === "") {
  //         this.spell_overrides.spellcasting_ability = "Don't Override";
  //       }
  //     }
  //   }
  // }

  get consumed_resource() {
    return this.spell_overrides.consumed_resource;
  }

  get resource_consumed() {
    const slot_override = this.spell_overrides.slot_override;
    if (slot_override === "Don't Override") {
      return "Slot";
    }
    return this.spell_overrides.consumed_resource.resource_consumed;
  }

  get potence_class() {
    return this.spell_overrides.potence_class;
  }

  get cast_at_level() {
    return this.spell_overrides.cast_at_level;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      saa_type: this.saa_type,
      spell_limit: this.spell_limit.toDBObj(),
      count: this.count.toDBObj(),
      suggestions: this.suggestions,
      spell_overrides: this.spell_overrides.toDBObj(),
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SpellsFromList) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof SpellModifier) {
      this.spell_limit = copyMe.spell_limit.clone();
      this.spell_overrides = copyMe.spell_overrides.clone();
    } else if (copyMe instanceof SpellDamageOptions || 
      copyMe instanceof ExtraEffectsFeature || 
      copyMe instanceof ExtraEffectTypeFeature) {
      this.spell_limit = copyMe.spell_limit.clone();
    }
  }

  safe_copy(copyMe: SpellsFromList) {
    this._name = copyMe._name;
    this.saa_type = copyMe.saa_type;
    this.spell_limit = copyMe.spell_limit.clone();
    this.count = copyMe.count.clone();
    this.suggestions = [...copyMe.suggestions];
    this.spell_overrides = copyMe.spell_overrides.clone();
  }

  copyFromIStringHashFeature(copyMe: IStringHashFeature): void {
    super.copy(copyMe);
    this.count.makeBasic();
    this.count.base = +copyMe.hash.count;
  }

  clone(drop_ids = false): SpellsFromList {
    const new_obj = new SpellsFromList();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * The big differences between SpellsFromList and this are: 
 * this always has specific spells (rather than being able to work with various parameters),
 * and you always get all of the spells on the list (rather than being able to choose a number of them),
 * and each one is able to specify its own cost 
 * (this is the big one. regular SpellsFromList all of them look to spell_overrides to see what it says for the cost,
 * and the only way they won't have the same cost is if it says Don't Override, and they are different levels of spells.
 * For this one each one gets its own cost override.)
 */
export class SpellsFromListWithCosts extends FloatingFeature {
  _name = "";

  spells: SpellWithOverridableCost[] = [];
  // Alterations
  spell_overrides: SpellOverrides = new SpellOverrides();
  
  constructor(obj?: any) {
    super(obj);
    this.feature_type = "Spells from List with Costs";
    if (obj) {
      if (obj.spells) {
        this.spells = obj.spells.map((o: any) => new SpellWithOverridableCost(o));
      }

      this.spell_overrides = obj.spell_overrides ? new SpellOverrides(obj.spell_overrides) : new SpellOverrides();
      if (obj.shared_cost_override) {
        this.shared_cost_override = new OverridableCost(obj.shared_cost_override);
      } else {
        this.shared_cost_override = new OverridableCost();
        if (this.slot_override !== "Mixed" && this.spell_consumed_resource !== "Mixed") {
          this.shared_cost_override.slot_override = this.slot_override;
          this.shared_cost_override.consumed_resource.resource_consumed = this.spell_consumed_resource;
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     if (obj.spells) {
  //       this.spells = obj.spells.map((o: any) => new SpellWithOverridableCost(o));
  //     }

  //     this.spell_overrides = obj.spell_overrides ? new SpellOverrides(obj.spell_overrides) : new SpellOverrides();
  //     this.shared_cost_override = obj.shared_cost_override ? new OverridableCost(obj.shared_cost_override) : new OverridableCost();
  //   }
  // }

  get potence_class() {
    return this.spell_overrides.potence_class;
  }

  get spell_limit() {
    const spell_limit = new SpellLimitation();
    spell_limit.spell_ids = this.spells.map(o => o.spell_id);
    return spell_limit;
  }

  get slot_override() {
    const all_consumed_resources = this.spells.map(o => o.cost_override);
    const consumed_resources = all_consumed_resources.filter(o => o.slot_override !== "At Will" && o.slot_override !== "At Will or Slot Upcast");
    if (consumed_resources.length > 0) {
      const unique = common_util.unique_array(consumed_resources.map(o => o.slot_override));
      if (unique.length === 1) {
        return unique[0];
      } else {
        return "Mixed";
      }
    } else if (this.spells.length > 0) {
      const unique = common_util.unique_array(all_consumed_resources.map(o => o.slot_override));
      if (unique.length === 1) {
        return unique[0];
      }
      return "At Will";
    } else {
      return "Don't Override";
    }
  }

  get spell_consumed_resource() {
    const slot_override = this.slot_override;
    if (slot_override !== "Mixed" && slot_override !== "At Will" && slot_override !== "At Will or Slot Upcast" && slot_override !== "Don't Override") {
      const all_consumed_resources = this.spells.map(o => o.cost_override);
      const consumed_resources = all_consumed_resources.filter(o => 
        o.slot_override !== "At Will" && o.slot_override !== "At Will or Slot Upcast"
      );
      const unique = common_util.unique_array(consumed_resources.map(o => o.consumed_resource.resource_consumed));
      if (unique.length === 1) {
        return unique[0];
      } else {
        return "Mixed";
      }
    } else {
      return slot_override;
    }
  }

  shared_cost_override: OverridableCost = new OverridableCost();

  get consumed_resource() {
    return this.spell_overrides.consumed_resource;
  }

  get resource_consumed() {
    const slot_override = this.spell_overrides.slot_override;
    if (slot_override === "Don't Override") {
      return "Slot";
    }
    return this.spell_overrides.consumed_resource.resource_consumed;
  }

  get cast_at_level() {
    return this.spell_overrides.cast_at_level;
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      spells: this.spells.map(o => o.toDBObj()),
      spell_overrides: this.spell_overrides.toDBObj(),
      shared_cost_override: this.shared_cost_override.toDBObj()
    };
  }

  copy(copyMe: FloatingFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof SpellsFromListWithCosts) {
      this.safe_copy(copyMe);
    } else if (copyMe instanceof SpellsFromList || 
      copyMe instanceof SpellModifier) {
      this.spell_overrides = copyMe.spell_overrides.clone();
      this.spells = [];
      for (const spell_id of copyMe.spell_limit.spell_ids) {
        const spell_with_overridable_cost = new SpellWithOverridableCost();
        spell_with_overridable_cost.spell_id = spell_id;
        spell_with_overridable_cost.cost_override = copyMe.spell_overrides.cost_override;
        this.spells.push(spell_with_overridable_cost);
      }
    } else if (copyMe instanceof SpellDamageOptions || 
      copyMe instanceof ExtraEffectsFeature || 
      copyMe instanceof ExtraEffectTypeFeature) {
      this.spells = [];
      for (const spell_id of copyMe.spell_limit.spell_ids) {
        const spell_with_overridable_cost = new SpellWithOverridableCost();
        spell_with_overridable_cost.spell_id = spell_id;
        this.spells.push(spell_with_overridable_cost);
      }
    }
  }

  safe_copy(copyMe: SpellsFromListWithCosts) {
    this._name = copyMe._name;
    this.spells = copyMe.spells.map(o => o.clone());
    this.spell_overrides = copyMe.spell_overrides.clone();
    this.shared_cost_override = copyMe.shared_cost_override.clone();
  }

  clone(drop_ids = false): SpellsFromListWithCosts {
    const new_obj = new SpellsFromListWithCosts();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class OverridableCost {
  slot_override: CostOverrideType = "Don't Override"; 
  
  consumed_resource: ConsumedResource = new ConsumedResource();
  
  constructor(obj?: any) {
    if (obj) {
      this.slot_override = obj.slot_override ? obj.slot_override : "Don't Override";
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
    }
  }

  toDBObj(): any {
    return {
      slot_override: this.slot_override,
      consumed_resource: this.consumed_resource.toDBObj()
    };
  }

  copy(copyMe: OverridableCost): void {
    this.slot_override = copyMe.slot_override;
    this.consumed_resource = copyMe.consumed_resource.clone();
  }

  clone(): OverridableCost {
    const new_obj = new OverridableCost();
    new_obj.copy(this);
    return new_obj;
  }
}

export class SpellWithOverridableCost {
  true_id = "";
  spell_id = "";
  cost_override: OverridableCost = new OverridableCost();
  cast_at_level: SpellLevel | "Don't Override" = "Don't Override";
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.spell_id = obj.spell_id ? obj.spell_id : "";
      this.cost_override = obj.cost_override ? new OverridableCost(obj.cost_override) : new OverridableCost();
      this.cast_at_level = obj.cast_at_level ? obj.cast_at_level : "Don't Override";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      spell_id: this.spell_id,
      cost_override: this.cost_override.toDBObj(),
      cast_at_level: this.cast_at_level
    };
  }

  copy(copyMe: SpellWithOverridableCost): void {
    this.true_id = copyMe.true_id;
    this.spell_id = copyMe.spell_id;
    this.cost_override = copyMe.cost_override.clone();
    this.cast_at_level = copyMe.cast_at_level;
  }

  clone(): SpellWithOverridableCost {
    const new_obj = new SpellWithOverridableCost();
    new_obj.copy(this);
    return new_obj;
  }
}